import * as z from "zod";
import { effortLevels } from "../aiProviders";
import { userFacingError } from "../authorization";
import { ensureWritable } from "../copyOnWrite";
import { zql } from "../generatedDrizzleSchema";
import { actorColumns } from "../rbac/roles";
import { stripNullBytes } from "../stripNullBytes";
import {
  assertAttachmentsSupported,
  attachmentSchema,
} from "./attachmentValidation";
import { mutator } from "./mutator";
import { clientGeneratableIdSchema } from "../id";
import {
  branchSchema,
  insertUserInterruptEntry,
  nextRespondingAt,
} from "./utils";

export const sendMessageSchema = z
  .object({
    entryId: clientGeneratableIdSchema,
    text: z.string().transform(stripNullBytes),
    isDictated: z.boolean().optional(),
    planMode: z.boolean().optional(),
    effort: z.enum(effortLevels).optional(),
    chatModel: z.string().optional(),
    actionKind: z
      .enum([
        "readme",
        "error",
        "connector",
        "investigate_step_test",
        "step_test_fixture_setup",
      ])
      .optional(),
    attachments: z.array(attachmentSchema).optional(),
    selectedStep: z.string().optional(),
    interruptEntryId: clientGeneratableIdSchema.optional(),
    clientSentAt: z.number().int().optional(),
    clientTimezone: z.string().optional(),
  })
  .merge(branchSchema);

export const sendMessage = mutator({
  redactKeysOnSecret: ["text"],
  schema: sendMessageSchema,
  resolve: {
    branch: { kind: "branch", from: "branchId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.branch.workflow.spaceId,
    },
  ],
  fn: async ({ tx, ctx, args }) => {
    const {
      entryId,
      text,
      isDictated,
      planMode,
      effort,
      chatModel,
      actionKind,
      attachments,
      interruptEntryId,
      branch,
      forkBranchId,
      forkCommitId,
      forkChatId,
      workingCommitId,
      workingChatId,
    } = args;
    if (!text.trim() && (!attachments || attachments.length === 0)) {
      throw userFacingError("Message must have text or attachments");
    }
    await assertAttachmentsSupported(tx, attachments, chatModel);
    const actor = actorColumns(ctx);

    const writable = await ensureWritable(
      tx,
      ctx,
      ctx.tenantId,
      {
        branchId: branch.id,
        forkBranchId,
        forkCommitId,
        forkChatId,
        workingCommitId,
        workingChatId,
      },
      branch
    );

    const [chat] = await tx.run(zql.chats.where("commitId", writable.commitId));
    if (!chat) throw userFacingError("Chat not found");

    const createdAt = Date.now();

    const lastEntries = await tx.run(
      zql.chatEntries
        .where("chatId", chat.id)
        .orderBy("createdAt", "desc")
        .limit(1)
    );
    const previousEntryId = lastEntries[0]?.id ?? null;

    if (interruptEntryId) {
      await insertUserInterruptEntry(tx, {
        id: interruptEntryId,
        chatId: chat.id,
        createdAt,
        previousEntryId,
        tenantId: ctx.tenantId,
      });
    }

    await tx.mutate.chatEntries.insert({
      id: entryId,
      chatId: chat.id,
      entryType: "UserMessage",
      createdAt: interruptEntryId ? createdAt + 1 : createdAt,
      previousEntryId: interruptEntryId ?? previousEntryId,
      tenantId: ctx.tenantId,
    });
    await tx.mutate.userMessages.insert({
      entryId,
      userId: actor.userId,
      serviceAccountId: actor.serviceAccountId,
      text,
      isDictated: isDictated ?? null,
      planMode: planMode ?? null,
      effort: effort ?? null,
      actionKind: actionKind ?? null,
      tenantId: ctx.tenantId,
    });

    const [wrBranch] = await tx.run(
      zql.workflowBranches.where("id", writable.branchId)
    );
    if (!wrBranch) throw userFacingError("Branch not found");
    const [workflow] = await tx.run(
      zql.workflows.where("id", wrBranch.workflowId)
    );
    if (workflow) {
      await tx.mutate.spaces.update({
        id: workflow.spaceId,
        updatedAt: createdAt,
      });
    }
    for (const att of attachments ?? []) {
      await tx.mutate.attachments.insert({
        id: att.id,
        entryId,
        fileName: att.fileName,
        mediaType: att.mediaType,
        sizeBytes: att.sizeBytes,
        tenantId: ctx.tenantId,
      });
    }

    // Contributors are the human facepile; a pure service account records none.
    if (actor.userId) {
      await tx.mutate.branchContributors.upsert({
        branchId: writable.branchId,
        workflowId: wrBranch.workflowId,
        userId: actor.userId,
        lastMessagedAt: createdAt,
        tenantId: ctx.tenantId,
      });
    }

    await tx.mutate.workflowBranches.update({
      id: writable.branchId,
      lastChatEntryAt: createdAt,
    });

    await tx.mutate.chats.update({
      id: chat.id,
      lastChatEntryType: "UserMessage",
      respondingAt: nextRespondingAt(chat.respondingAt),
      awaitingAction: false,
    });

    await tx.mutate.workflows.update({
      id: wrBranch.workflowId,
      updatedAt: createdAt,
    });
  },
});
